import { useHistory } from "react-router-dom";
import type { BusinessBillingInfo, BusinessBillingInfoUpdate } from "lens-platform-sdk";
import { TypographyFontPrimary, TypographySizeBase } from "@k8slens/lds-tokens/lib/web/tokens.base";
import { AliasColorTextAccent, AliasColorTextPrimary } from "@k8slens/lds-tokens/lib/web/tokens.dark";
import { getData } from "countries-data-list";

import { transparentizeHSL } from "src/utils/transparentize-hsl";

import { type Option } from "src/components/Combobox/Combobox";

import { RecurlyStyleProps, type BillingDetailsForm, type PaymentMethod, type RecurlyData } from "./types";

export const recurlyStyle = (
  { textAlign = "left", placeholder = "" }: RecurlyStyleProps = {},
  focused?: boolean,
  prefilled?: boolean,
) => ({
  fontFamily: TypographyFontPrimary,
  fontSize: TypographySizeBase,
  fontColor: focused ? AliasColorTextAccent : AliasColorTextPrimary,
  textAlign,
  placeholder: {
    color: prefilled ? AliasColorTextPrimary : transparentizeHSL(AliasColorTextPrimary, 0.5),
    content: placeholder,
  },
});

export const paymentMethodOptions: Array<{ id: PaymentMethod; label: string }> = [
  { id: "credit_card", label: "Credit card" },
  { id: "invoice", label: "Invoice" },
];

export const countryOptions: Array<Option> = getData().map(({ code, name }) => ({
  id: code,
  label: name,
}));

export const billingDetailsFormDefaults = {
  firstName: { value: "", valid: true, required: true },
  lastName: { value: "", valid: true, required: true },
  paymentMethod: { value: undefined, valid: true, required: true },
  addressCity: { value: "", valid: true, required: true },
  addressCountry: { value: undefined, valid: true, required: true },
  addressPostalCode: { value: "", valid: true, required: true },
  addressRegion: { value: "", valid: true, required: false },
  addressStreet1: { value: "", valid: true, required: true },
  addressStreet2: { value: "", valid: false, required: false },
  company: { value: "", valid: true, required: true },
  email: { value: "", valid: true, required: true },
  vatNumber: { value: "", valid: true, required: false },
};

export const mapBillingInfoToForm = (values: BillingDetailsForm, billingInfo: BusinessBillingInfo) => ({
  firstName: {
    ...billingDetailsFormDefaults.firstName,
    ...values.firstName,
    value: billingInfo.firstName || "",
  },
  lastName: {
    ...billingDetailsFormDefaults.lastName,
    ...values.lastName,
    value: billingInfo.lastName || "",
  },
  paymentMethod: {
    ...billingDetailsFormDefaults.paymentMethod,
    ...values.paymentMethod,
    value: (billingInfo.type as "credit_card" | "invoice") || "credit_card",
  },
  addressCity: {
    ...billingDetailsFormDefaults.addressCity,
    ...values.addressCity,
    value: billingInfo.address?.city || "",
  },
  addressCountry: {
    ...billingDetailsFormDefaults.addressCountry,
    ...values.addressCountry,
    value: billingInfo.address?.country || "US",
  },
  addressPostalCode: {
    ...billingDetailsFormDefaults.addressPostalCode,
    ...values.addressPostalCode,
    value: billingInfo.address?.postalCode || "",
  },
  addressRegion: {
    ...billingDetailsFormDefaults.addressRegion,
    ...values.addressRegion,
    value: billingInfo.address?.region || "",
  },
  addressStreet1: {
    ...billingDetailsFormDefaults.addressStreet1,
    ...values.addressStreet1,
    value: billingInfo.address?.street1 || "",
  },
  addressStreet2: {
    ...billingDetailsFormDefaults.addressStreet2,
    ...values.addressStreet2,
    value: billingInfo.address?.street2 || "",
  },
  company: {
    ...billingDetailsFormDefaults.company,
    ...values.company,
    value: billingInfo.company || "",
  },
  email: {
    ...billingDetailsFormDefaults.email,
    ...values.email,
    value: billingInfo.email || "",
  },
  vatNumber: {
    ...billingDetailsFormDefaults.vatNumber,
    ...values.vatNumber,
    value: billingInfo.vatNumber || "",
  },
});

export const mapFormToBillingInfoUpdate = (
  values: BillingDetailsForm,
  recurlyData?: RecurlyData,
  paymentMethod?: BusinessBillingInfo["paymentMethod"],
): BusinessBillingInfoUpdate => {
  const isCreditCard = values.paymentMethod.value === "credit_card";
  const isAustralia = values.addressCountry.value === "AU";

  return {
    firstName: values.firstName.value || null,
    lastName: values.lastName.value || null,
    type: values.paymentMethod.value || null,
    address: {
      city: values.addressCity.value || null,
      country: values.addressCountry.value || null,
      postalCode: values.addressPostalCode.value || null,
      region: values.addressRegion.value || null,
      street1: values.addressStreet1.value || null,
      street2: values.addressStreet2.value || null,
      phone: null, // Phone is not included in the form
    },
    company: values.company.value || null,
    email: values.email.value || null,
    vatNumber: isAustralia ? null : values.vatNumber.value || null,
    ...(!isCreditCard
      ? {
          token: null,
        }
      : recurlyData
      ? { token: recurlyData.id }
      : {}),
    paymentMethod: isCreditCard
      ? recurlyData
        ? {
            cardType: recurlyData.card.brand,
            firstSix: recurlyData.card.first_six,
            lastFour: recurlyData.card.last_four,
            lastTwo: recurlyData.card.last_four.slice(-2),
            expMonth: recurlyData.card.exp_month,
            expYear: recurlyData.card.exp_year,
          }
        : paymentMethod
      : undefined,
  } as any as BusinessBillingInfoUpdate;
};

export const createBillingInfoQueryParams = (updateBillingInfo: BusinessBillingInfoUpdate): string => {
  const searchParams = new URLSearchParams();

  searchParams.set("billing-info", encodeURIComponent(JSON.stringify(updateBillingInfo)));

  return searchParams.toString();
};

export const parseBillingInfoQueryParams = (
  location: ReturnType<typeof useHistory>["location"],
): BusinessBillingInfoUpdate | null => {
  const searchParams = new URLSearchParams(location.search);

  const billingInfo = searchParams.get("billing-info");

  if (!billingInfo) {
    return null;
  }

  try {
    return JSON.parse(decodeURIComponent(billingInfo)) as BusinessBillingInfoUpdate;
  } catch (error) {
    console.error("Failed to parse billing info from query params:", error);

    return null;
  }
};

export const ccFormFieldsKeys = ["number", "month", "year", "cvv", "first_name", "last_name"];
